// ÁTTEKINTÉS — a vezetői KPI-sáv a három összkép-kártya alatt. Négy kérdés, egy-egy számmal:
//
//  • HASZONKULCS — az egyenleg a bevétel arányában (a három kártya számaiból — lezárt hónapon a tény-bevételből;
//    arány, nem új pénz-összeg);
//  • KINTLÉVŐSÉG — a kiállított, ki nem fizetett bizonylatok bruttója MÁRA, benne a lejártak (BFF: `receivables`);
//  • ÜGYFÉL-KONCENTRÁCIÓ — a 10 legnagyobb ügyfél hányad-része a hónap bevételéből (kockázat-jelző: minél
//    nagyobb, annál jobban függ a bevétel néhány ügyféltől);
//  • KÖVETKEZŐ HÓNAP — a valódi következő naptári hónap várható bevétele (terv).
//
// Az arányok `Number`-rel számolnak — ez MEGJELENÍTÉS (százalék), nem pénz-összeg; a pénz Decimal-string marad.

import type { ExpenseAnalytics, RevenueForecastResponse } from '@atlas/contracts';
import { subtractDecimals } from '@atlas/shared';
import {
  BanknotesIcon,
  CalendarDaysIcon,
  ReceiptPercentIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { StatCard } from '@/components/shared/stat-card';
import { summaryRevenue } from '@/features/finance/summary-revenue';
import { formatMoney } from '@/lib/format-money';
import { formatPercent } from '@/lib/format-percent';
import { currentMonth, formatMonthLabel, shiftMonth } from '@/lib/month';

/** Hány ügyfél számít a koncentrációba — ugyanannyi, amennyit a kimutatás „top ügyfelek" bontása ad. */
const TOP_CLIENTS = 10;

/** Arány százalékban, egy tizedessel; értelmes bázis nélkül `null`. */
function pct(part: number, whole: number): number | null {
  return whole > 0 ? Math.round((part / whole) * 1000) / 10 : null;
}

export function OverviewKpis({
  forecast,
  analytics,
}: {
  forecast: RevenueForecastResponse;
  analytics: ExpenseAnalytics;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const fmt = (v: string, currency = 'HUF'): string => formatMoney(v, currency, locale);
  const none = t('finance.kpi.none');

  // HASZONKULCS: ugyanazokból a számokból, mint a három kártya (a cél-hónap forint-kiadása a trend utolsó oszlopa
  // — bitre egyezik a kártya kiadás-számával, a BFF tesztje védi)
  const revenue = summaryRevenue(forecast).amount;
  const expense = analytics.trend.at(-1)?.netAmount ?? '0';
  const margin = pct(Number(subtractDecimals(revenue, expense)), Number(revenue));

  // KINTLÉVŐSÉG: a forint-sor a fő szám; a devizás kintlévőség a leírásban, átváltás nélkül. Egy régebbi
  // BFF-válaszból a mező hiányozhat (deploy-ablak) — akkor „nincs adat".
  const receivables = 'receivables' in forecast ? forecast.receivables : null;
  const huf = receivables?.totals.find((r) => r.currency === 'HUF');
  const foreign = (receivables?.totals ?? []).filter((r) => r.currency !== 'HUF');

  // KONCENTRÁCIÓ — ugyanazon az alapon, mint a kártya: LEZÁRT hónapon a TÉNY (a tény ügyfél-bontásából, a hónap
  // tény-összegéhez mérve; a besorolatlan — név nélküli — sor a nevezőben benne van, a top-listában nem),
  // NYITOTT hónapon a TERV (a top 10 ügyfél a kártya terv-számához). A nevező mindig a hónap TELJES bevétele, így
  // az arány nem csúszhat 100% fölé (korábban a szolgáltatás-bontás összege volt, ami a név nélküli szolgáltatást
  // kihagyta, az ügyfél-bontás viszont nem — review-lelet).
  const closed = forecast.actual.closed;
  const topClients = closed
    ? forecast.actual.byClient
        .filter((c) => c.name !== null)
        .slice(0, TOP_CLIENTS)
        .reduce((a, c) => a + Number(c.amount), 0)
    : forecast.charts.byClient.slice(0, TOP_CLIENTS).reduce((a, c) => a + Number(c.total), 0);
  const concentration = pct(
    topClients,
    Number(closed ? forecast.actual.total : forecast.stats.selectedMonth),
  );

  const next = shiftMonth(currentMonth(), 1);

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <StatCard
        icon={ReceiptPercentIcon}
        title={t('finance.kpi.margin')}
        value={margin === null ? none : formatPercent(margin, locale)}
        description={t('finance.kpi.marginDesc')}
      />
      <StatCard
        icon={BanknotesIcon}
        title={t('finance.kpi.receivables')}
        value={huf ? fmt(huf.outstanding) : receivables ? fmt('0') : none}
        {...(huf && huf.overdueCount > 0
          ? {
              subtitle: t('finance.kpi.overdue', {
                amount: fmt(huf.overdue),
                count: huf.overdueCount,
              }),
            }
          : {})}
        description={
          foreign.length > 0
            ? t('finance.kpi.receivablesForeign', {
                list: foreign.map((f) => fmt(f.outstanding, f.currency)).join(', '),
              })
            : t('finance.kpi.receivablesDesc')
        }
      />
      <StatCard
        icon={UserGroupIcon}
        title={t('finance.kpi.concentration')}
        value={concentration === null ? none : formatPercent(concentration, locale)}
        description={t(
          closed ? 'finance.kpi.concentrationDescActual' : 'finance.kpi.concentrationDesc',
          { count: TOP_CLIENTS },
        )}
      />
      <StatCard
        icon={CalendarDaysIcon}
        title={t('finance.kpi.nextMonth')}
        value={fmt(forecast.stats.nextMonth)}
        description={t('finance.kpi.nextMonthDesc', { month: formatMonthLabel(next, locale) })}
      />
    </div>
  );
}
